import React, { FC } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface MarkdownProps {
  children?: string;
}

/**
 * Renders post/preview body copy as GitHub-flavored Markdown. Raw HTML is NOT
 * rendered (react-markdown escapes it by default), so this is safe by design —
 * no dangerouslySetInnerHTML, unlike the old html-react-parser path.
 */
const Markdown: FC<MarkdownProps> = ({ children }) => {
  const theme = useTheme();
  if (!children) return null;

  return (
    <Box
      sx={{
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-child': { mb: 0 },
        '& p': { m: 0, mb: 1 },
        '& a': { color: theme.palette.primary.main },
        '& img': { maxWidth: '100%', borderRadius: 'var(--sh-md)' },
        '& ul, & ol': { pl: 3, m: 0, mb: 1 },
        '& li': { mb: 0.25 },
        '& h1, & h2, & h3, & h4': { fontSize: '1.05em', fontWeight: 700, m: 0, mb: 0.5 },
        '& blockquote': {
          borderLeft: `3px solid ${theme.tokens.colors.cardBorder}`,
          pl: 1.5,
          ml: 0,
          my: 1,
          color: theme.tokens.colors.text3,
        },
        '& code': {
          fontFamily: theme.tokens.typography.monoFontFamily,
          fontSize: '0.9em',
          backgroundColor: theme.tokens.colors.backgroundCardHover,
          px: 0.5,
          borderRadius: '4px',
        },
        '& pre': {
          overflowX: 'auto',
          p: 1.25,
          my: 1,
          borderRadius: 'var(--sh-md)',
          backgroundColor: theme.tokens.colors.backgroundCardHover,
        },
        '& pre code': { backgroundColor: 'transparent', px: 0 },
        '& table': { borderCollapse: 'collapse', display: 'block', overflowX: 'auto' },
        '& th, & td': { border: `1px solid ${theme.tokens.colors.cardBorder}`, px: 1, py: 0.5 },
        '& hr': { border: 'none', borderTop: `1px solid ${theme.tokens.colors.cardBorder}`, my: 1.5 },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ node, ...props }) => <Link {...props} target="_blank" rel="noopener noreferrer" />,
        }}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
};

export default Markdown;
